
import { useMemo, useState, useRef, useEffect } from "react";
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
} from "@mui/lab";
import {
    Box,
    Paper,
    Typography,
    Link,
    TextField,
    Autocomplete,
    Chip,
    Stack,
    Divider,
    IconButton,
    Container,
    Tabs,
    Tab,
    FormLabel,
    useTheme,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Page from "../../component/Page";
import NextBreadcrumbs from "../../component/NextBreadcrumbs";
import { KentuckyHistoryEvents } from "../../utils/history";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import citations from "../../utils/citations";
import NextLink from "next/link";



/* -------------------------------- Helpers ------------------------------ */
const addDecadeTag = (items) =>
    items.map((item) => {
        if (item.decade) return item;
        const match = item.title.match(/\d{4}/);
        const year = match ? Number(match[0]) : undefined;
        const decade = year ? `${Math.floor(year / 10) * 10}` : "Unknown";
        return { ...item, decade };
    });

const groupByDecade = (items) => {
    const groups = {};
    items.forEach((ev) => {
        (groups[ev.decade] ??= { title: ev.decade, items: [] }).items.push(ev);
    });
    return Object.values(groups).sort((a, b) => parseInt(a.title) - parseInt(b.title));
};

const mergeTimelineEvents = (localEvents = [], remoteEvents = [], removedKeys = []) => {
    const keyFor = (ev) => `${ev?.title ?? ""}::${ev?.cardTitle ?? ""}`;
    const removedSet = new Set(removedKeys);

    const merged = new Map(
        localEvents
            .filter((ev) => !removedSet.has(keyFor(ev)))
            .map((ev) => [keyFor(ev), ev])
    );

    // Remote items overwrite matching local records and add new ones.
    remoteEvents.forEach((ev) => {
        merged.set(keyFor(ev), ev);
    });

    return Array.from(merged.values());
};

/* -------------------------------- Component ---------------------------- */
const KentuckyTimeline = () => {
    /* Hooks */
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    /* Data prep */
    const [sourceEvents, setSourceEvents] = useState(KentuckyHistoryEvents);

    useEffect(() => {
        let active = true;

        const loadTimeline = async () => {
            try {
                const response = await fetch("/api/getTimelineData");
                if (!response.ok) return;

                const json = await response.json();
                if (active && Array.isArray(json?.data)) {
                    const removedKeys = Array.isArray(json?.deletedKeys) ? json.deletedKeys : [];
                    setSourceEvents(mergeTimelineEvents(KentuckyHistoryEvents, json.data, removedKeys));
                }
            } catch (error) {
                // Keep local fallback data when remote timeline is unavailable.
            }
        };

        loadTimeline();

        return () => {
            active = false;
        };
    }, []);

    const events = useMemo(() => addDecadeTag(sourceEvents), [sourceEvents]);
    const eventTypes = useMemo(() => Array.from(new Set(events.map((e) => e.eventType))).sort(), [events]);
    const decades = useMemo(() => groupByDecade(events), [events]);

    /* Decade refs for jump‑to */
    const decadeRefs = useRef({});

    useEffect(() => {
        // ensure keys exist after decades recomputed
        decades.forEach((d) => {
            if (!(d.title in decadeRefs.current)) decadeRefs.current[d.title] = null;
        });
    }, [decades]);

    const [selectedTypes, setSelectedTypes] = useState([]);
    const [search, setSearch] = useState("");

    /* Filtered timeline list */
    const timelineData = useMemo(() => {
        const hit = (ev) => {
            const typeOk = selectedTypes.length === 0 || selectedTypes.includes(ev.eventType);
            const textOk =
                search.trim() === "" ||
                [ev.cardTitle, ev.cardSubtitle, ev.cardDetailedText, ev.title]
                    .some((t) => t && t.toLowerCase().includes(search.toLowerCase()));
            return typeOk && textOk;
        };
        const out = [];
        decades.forEach((dec) => {
            const visible = dec.items.filter(hit);
            if (visible.length > 0) {
                out.push({ isHeader: true, decade: dec.title });
                out.push(...visible);
            }
        });
        return out;
    }, [decades, selectedTypes, search]);

    const lastIndex = timelineData.length - 1;

    /* Render helpers */
    const renderHeader = (dec, position) => (
        <TimelineItem
            key={`header-${dec}`}
            position={position}
            ref={(el) => {
                decadeRefs.current[dec] = el;
            }}
        >
            <TimelineSeparator>
                <TimelineDot variant="outlined" />
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent sx={{ py: 2 }}>
                <Divider textAlign="left" sx={{ mb: 1 }}>
                    <Typography variant="h5">{dec}</Typography>
                </Divider>
            </TimelineContent>
        </TimelineItem>
    );

    const renderUrls = (urls = []) => {
        if (urls.length === 0) return null;
        return (
            <Box sx={{ mt: 1 }}>
                {urls.map((u) => (
                    <Link key={u} href={u} target="_blank" rel="noopener noreferrer" sx={{ mr: 1 }}>
                        More info
                    </Link>
                ))}
            </Box>
        );
    };

    const renderEvent = (ev, isLast, position) => (
        <TimelineItem key={`${ev.title}-${ev.cardTitle}`} position={position}>
            <TimelineSeparator>
                <TimelineDot color="primary" />
                {!isLast && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 2 }}>
                <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6">{ev.cardTitle}</Typography>
                    {ev.cardSubtitle && (
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {ev.cardSubtitle}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {ev.cardDetailedText}
                    </Typography>
                    {renderUrls(ev.url)}
                    <Typography variant="caption" color="text.secondary" display="block">
                        {ev.title} • {ev.eventType}
                    </Typography>
                </Paper>
            </TimelineContent>
        </TimelineItem>
    );

    /* JSX */
    // Insert a special 2025 recap card at the top of the timeline
    const recap2025 = {
        title: "2025",
        cardTitle: "2025 Recap",
        cardSubtitle: "A look back at 2025",
        cardDetailedText: "Click to view the 2025 recap.",
        eventType: "Recap",
        url: ["/recap/2025"],
        decade: "2020",
        isRecap2025: true,
    };

    const timelineWithRecap = useMemo(() => {
        // Insert recap card at the top if not filtered out
        if (
            (selectedTypes.length === 0 || selectedTypes.includes("Recap")) &&
            (search.trim() === "" || "2025 recap".includes(search.toLowerCase()))
        ) {
            return [...timelineData, recap2025];
        }
        return timelineData;
    }, [timelineData, selectedTypes, search]);

    return (
        <Page >
            <Container maxWidth="xl" sx={{ py: 5 }}>
                <NextBreadcrumbs title="Timeline" parentLabel="KY Climate" parentHref="/timeline" />
                <Typography variant="h3">KY Climate</Typography>
                <Tabs value="timeline" sx={{ mt: 1, mb: 2 }}>
                    <Tab label="Timeline" value="timeline" component={NextLink} href="/timeline" />
                    <Tab label="Statistics and Records" value="stats" component={NextLink} href="/stats" />
                </Tabs>
                <Typography variant="body1" gutterBottom>
                    External Contributors: WKU student Grant Stromquist
                </Typography>
                <Typography variant="body2" gutterBottom sx={{ maxWidth: { md: "55%" } }}>
                    This material is, in part, based upon work supported by the National Science Foundation under Cooperative Agreement No. 2344533 and the Commonwealth of Kentucky. Any opinions, findings, and conclusions or recommendations expressed in this material are those of the authors and do not necessarily reflect the views of the National Science Foundation.
                </Typography>
                <Paper sx={{ p: 1.5, my: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems="center"
                        sx={{ width: "100%" }}
                    >
                        <Autocomplete
                            fullWidth
                            multiple
                            options={eventTypes}
                            size="small"
                            getOptionLabel={(opt) => opt}
                            value={selectedTypes}
                            onChange={(e, val) => setSelectedTypes(val)}
                            renderValue={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option} {...getTagProps({ index })} key={option} />
                                ))
                            }
                            sx={{ flex: 1, minWidth: 0 }}
                            renderInput={(params) => <TextField {...params} label="Filter by type" />}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Search…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ flex: 1, minWidth: 0 }}
                            slotProps={{
                                input: {
                                    startAdornment: <SearchIcon fontSize="small" />,
                                    endAdornment: search && (
                                        <IconButton size="small" onClick={() => setSearch("")}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    ),

                                }
                            }}
                        />
                        <Box sx={{ flex: 2, minWidth: 0, position: "relative", width: "100%" }}>
                            <FormLabel
                                component="legend"
                                sx={{
                                    position: "absolute",
                                    top: -8,
                                    left: 12,
                                    backgroundColor: "white",
                                    px: 0.5,
                                    fontSize: "0.75rem",
                                    zIndex: 1,
                                }}
                            >
                                Jump to
                            </FormLabel>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: "rgba(0, 0, 0, 0.23)",
                                    borderRadius: 1,
                                    minHeight: 38,
                                    width: "100%",
                                    overflowX: "auto",
                                }}
                            >
                                <Tabs
                                    value={false}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    aria-label="Jump to year"
                                    sx={{
                                        minHeight: 38,
                                        "& .MuiTab-root": {
                                            minHeight: 38,
                                            padding: "4px 8px",
                                            fontSize: "0.75rem",
                                            minWidth: 45,
                                        },
                                    }}
                                >
                                    {[...decades].reverse().map((g) => (
                                        <Tab
                                            key={g.title}
                                            label={g.title}
                                            onClick={() => decadeRefs.current[g.title]?.scrollIntoView({ behavior: "smooth", block: "center" })}
                                        />
                                    ))}
                                </Tabs>
                            </Box>
                        </Box>
                    </Stack>
                </Paper>

                <Box component={Paper} sx={{ p: 2, height: "50vh", overflowY: "auto" }}>
                    <Timeline
                        position={isSmall ? "right" : undefined}
                        sx={
                            isSmall
                                ? {
                                    pl: 0,
                                    "& .MuiTimelineItem-root::before": { flex: 0, padding: 0 },
                                }
                                : undefined
                        }
                    >
                        {(() => {
                            let currentPosition = isSmall ? "right" : "left";
                            return timelineWithRecap.map((item, idx) => {
                                if (item.isHeader) {
                                    if (!isSmall) currentPosition = currentPosition === "left" ? "right" : "left";
                                    return renderHeader(item.decade, isSmall ? "right" : currentPosition);
                                }
                                if (item.isRecap2025) {
                                    // Render the 2025 recap card in red
                                    return (
                                        <TimelineItem key="2025-recap" position={isSmall ? "right" : currentPosition}>
                                            <TimelineSeparator>
                                                <TimelineDot sx={{ bgcolor: "#A50034" }} />
                                                <TimelineConnector />
                                            </TimelineSeparator>
                                            <TimelineContent sx={{ py: 2 }}>
                                                <Paper
                                                    elevation={4}
                                                    sx={{
                                                        p: 2,
                                                        border: "2px solid",
                                                        borderColor: "#9A1A20",
                                                        bgcolor: "#9A1A20",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    <Typography variant="h6" sx={{ color: "#fff" }}>
                                                        2025 Recap
                                                    </Typography>
                                                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: "#fff" }}>
                                                        A look back at 2025: Winter weather, floods, tornadoes, and droughts.
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Link
                                                            href="/recap/2025"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{ color: "#fff", textDecoration: "underline" }}
                                                        >
                                                            View 2025 Recap
                                                        </Link>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: "#fff" }} display="block">
                                                        2025 • Recap
                                                    </Typography>
                                                </Paper>
                                            </TimelineContent>
                                        </TimelineItem>
                                    );
                                }
                                const isLast = idx === timelineWithRecap.length - 1;
                                return renderEvent(item, isLast, isSmall ? "right" : currentPosition);
                            });
                        })()}
                    </Timeline>
                </Box>
                {/* <Typography variant="h4">Resources</Typography> */}

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">View Citations</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            {citations.map((citation, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={
                                            <Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        wordBreak: "break-word",
                                                        whiteSpace: "normal",
                                                    }}
                                                >
                                                    {citation.citation}
                                                </Typography>
                                                {citation.url !== "" && (
                                                    <Link href={citation.url} target="_blank" rel="noopener noreferrer">
                                                        <Typography variant="body1" sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                                                            {citation.url}
                                                        </Typography>
                                                    </Link>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </Container>
        </Page>
    );
};


export default KentuckyTimeline;
